import { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, IconButton, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { B2BClientService, B2BOrderService } from '../../../services/B2BServices';
import { getLocations } from '../../../services/LocationService';
import AuthContext from '../../../context/AuthContext';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { DISCOUNT_TYPE } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

const AddB2BOrderModal = ({
  open,
  onClose,
  onSave,
  fixedDestinationLocationId = null,
  fixedDestinationLocationName = ''
}) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const isDestinationScoped = Boolean(fixedDestinationLocationId);

  const [order, setOrder] = useState({
    clientId: '',
    clientName: '',
    orderDate: dayjs(),
    deliveryDate: null,
    status: 'PENDING',
    notes: '',
    items: [],
    creditDays: 30, // Valor por defecto
    paymentStatus: 'UNPAID',
    paymentAmount: '',
    paymentMethod: 'TRANSFER',
    paymentNotes: '',
    branchId: '',
    branchName: '',
    destinationLocationId: ''
  });

  const [errors, setErrors] = useState({});
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unitPrice: '',
    discountType: DISCOUNT_TYPE.PERCENTAGE,
    discountValue: '',
    totalPrice: 0,
    isDiscountManuallyModified: false // Flag para indicar si el usuario modificó el descuento
  });

  // Estados para datos reales
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [destinationLocations, setDestinationLocations] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      fetchClients();
      fetchLocations();
      if (isDestinationScoped) {
        setOrder(prev => ({
          ...prev,
          destinationLocationId: fixedDestinationLocationId,
          paymentStatus: 'UNPAID',
          paymentAmount: '',
          paymentNotes: ''
        }));
      }
    }
  }, [open, isDestinationScoped, fixedDestinationLocationId]);

  useEffect(() => {
    if (!open || !isDestinationScoped || clients.length === 0) return;

    const matchingClients = clients.filter(client =>
      client.clientType === 'FRANCHISE' &&
      Number(client.defaultDestinationLocationId) === Number(fixedDestinationLocationId)
    );

    if (matchingClients.length === 1 && !order.clientId) {
      const client = matchingClients[0];
      setOrder(prev => ({
        ...prev,
        clientId: client.id,
        clientName: client.nombreFantasia || client.razonSocial || client.businessName || '',
        creditDays: client.creditDays || prev.creditDays,
        destinationLocationId: fixedDestinationLocationId
      }));
    }
  }, [open, isDestinationScoped, clients, fixedDestinationLocationId, order.clientId]);

  // Cargar productos cuando cambia la sucursal o el cliente
  useEffect(() => {
    if (open && order.branchId && order.clientId) {
      fetchProducts();
    } else if (open && (!order.branchId || !order.clientId)) {
      // Limpiar productos si no hay cliente o sucursal seleccionada
      setProducts([]);
    }
  }, [open, order.branchId, order.clientId]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      if (isDestinationScoped) {
        const response = await B2BOrderService.getFranchiseClients(fixedDestinationLocationId);
        setClients(Array.isArray(response) ? response : []);
      } else {
        const response = await B2BClientService.getClients(1, 100000, '');
        setClients(Array.isArray(response?.clients) ? response.clients : []);
      }
    } catch (error) {
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const [originLocations, accessibleLocations] = await Promise.all([
        B2BOrderService.getAvailableBranches(),
        getLocations()
      ]);
      setLocations(originLocations || []);
      setDestinationLocations((accessibleLocations || []).filter(location => !location.isPlant));
    } catch (error) {
      setLocations([]);
      setDestinationLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchProducts = async () => {
    if (!order.branchId || !order.clientId) {
      setProducts([]);
      return;
    }

    try {
      setLoadingProducts(true);
      const response = await B2BClientService.getOrderProducts(order.clientId, order.branchId);
      setProducts(response.products || []);
    } catch (error) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setOrder(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleItemFieldChange = (field, value) => {
    setNewItem(prev => {
      const updated = { ...prev, [field]: value };
      
      // Marcar como modificado manualmente si el usuario cambia el descuento
      if (field === 'discountValue' || field === 'discountType') {
        updated.isDiscountManuallyModified = true;
      }
      
      if (field === 'quantity' || field === 'unitPrice' || field === 'discountValue' || field === 'discountType') {
        const subtotal = updated.quantity * updated.unitPrice;
        let discountAmount = 0;
        
        // Validar que discountValue sea un número válido
        const discountValue = parseFloat(updated.discountValue) || 0;
        
        if (updated.discountType === DISCOUNT_TYPE.PERCENTAGE) {
          discountAmount = subtotal * (discountValue / 100);
        } else if (updated.discountType === DISCOUNT_TYPE.AMOUNT) {
          // El amount es por producto, entonces se multiplica por la cantidad
          const totalDiscountAmount = discountValue * updated.quantity;
          discountAmount = Math.min(totalDiscountAmount, subtotal); // No puede ser mayor al subtotal
        }
        
        updated.totalPrice = subtotal - discountAmount;
        console.log(`Cálculo total - Subtotal: ${formatMoney(subtotal)}, Descuento: ${formatMoney(discountAmount)}, Total: ${formatMoney(updated.totalPrice)}`);
      }
      console.log(`newItem actualizado:`, updated);
      return updated;
    });
  };

  // Función para obtener el precio correcto (siempre precio de sucursal)
  const getCorrectPrice = (productId, originalPrice) => {
    // Siempre usar el precio original de la sucursal
    return originalPrice;
  };

  // Función para obtener el descuento correcto considerando excepciones
  const getCorrectDiscount = (productId, clientId, branchPrice) => {
    const product = products.find(p => p.id === productId);
    return { 
      value: product?.discountValue || 0,
      type: product?.discountType || DISCOUNT_TYPE.PERCENTAGE
    };
  };

  // Función para validar si el formulario de item está completo
  const isItemFormValid = () => {
    return newItem.productId && 
           newItem.quantity && 
           newItem.quantity > 0 &&
           newItem.unitPrice && 
           newItem.unitPrice > 0;
  };

  // Función para validar si el formulario principal está completo
  const isFormValid = () => {
    const basicValidation = order.clientId && 
                           order.branchId && 
                           order.orderDate && 
                           order.items.length > 0;
    
    // Si el estado de pago es PARTIAL, también debe tener un monto y método
    if (order.paymentStatus === 'PARTIAL') {
      return basicValidation && 
             order.paymentAmount && 
             parseFloat(order.paymentAmount) > 0 &&
             order.paymentMethod;
    }
    
    // Si el estado de pago es PAID, solo necesita método (el monto es el total)
    if (order.paymentStatus === 'PAID') {
      return basicValidation && order.paymentMethod;
    }
    
    return basicValidation;
  };

  const handleAddItem = () => {
    // Validar campos obligatorios
    if (!newItem.productId) {
      SnackbarUtils.error(t('product_required'));
      return;
    }
    if (!newItem.quantity || newItem.quantity <= 0) {
      SnackbarUtils.error(t('quantity_required'));
      return;
    }
    if (!newItem.unitPrice || newItem.unitPrice <= 0) {
      SnackbarUtils.error(t('price_required'));
      return;
    }
    
    if (newItem.productId && newItem.quantity > 0 && newItem.unitPrice > 0) {
      const product = products.find(p => p.id === newItem.productId);
      
      // Obtener el precio correcto (siempre precio de sucursal)
      const correctPrice = getCorrectPrice(newItem.productId, newItem.unitPrice);
      
      // Obtener el descuento correcto considerando excepciones
      const discountInfo = getCorrectDiscount(newItem.productId, order.clientId, newItem.unitPrice);
      
      const item = {
        ...newItem,
        productName: product.name,
        unitPrice: correctPrice,
        // Usar los valores de descuento que tiene el usuario (pueden ser modificados manualmente)
        discountValue: newItem.discountValue,
        discountType: newItem.discountType,
        totalPrice: newItem.totalPrice
      };
      
      setOrder(prev => ({
        ...prev,
        items: [...prev.items, item]
      }));
      setNewItem({
        productId: '',
        productName: '',
        quantity: '',
        unitPrice: '',
        discountType: DISCOUNT_TYPE.PERCENTAGE,
        discountValue: '',
        totalPrice: 0,
        isDiscountManuallyModified: false
      });
    }
  };

  const handleRemoveItem = (index) => {
    setOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const validateFields = () => {
    const newErrors = {};

    if (!order.clientId) {
      newErrors.clientId = t('client_required');
    }

    if (!order.branchId) {
      newErrors.branchId = t('branch_required');
    }

    if (!order.orderDate) {
      newErrors.orderDate = t('order_date_required');
    }

    if (order.items.length === 0) {
      newErrors.items = t('items_required');
    }

    if (selectedClient?.clientType === 'FRANCHISE' && !selectedClient?.defaultDestinationLocationId) {
      newErrors.destinationLocationId = 'El cliente no tiene una franquicia asignada';
    }

    if (isDestinationScoped && Number(selectedClient?.defaultDestinationLocationId) !== Number(fixedDestinationLocationId)) {
      newErrors.clientId = 'Seleccioná el cliente B2B asociado a esta franquicia';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    return order.items.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const handleSave = async () => {
    const newErrors = {};
    
    if (!order.clientId) {
      newErrors.clientId = t('client_required');
    }
    
    if (order.items.length === 0) {
      newErrors.items = t('items_required');
    }

    if (selectedClient?.clientType === 'FRANCHISE' && !selectedClient?.defaultDestinationLocationId) {
      newErrors.destinationLocationId = 'El cliente no tiene una franquicia asignada';
    }

    if (isDestinationScoped && Number(selectedClient?.defaultDestinationLocationId) !== Number(fixedDestinationLocationId)) {
      newErrors.clientId = 'Seleccioná el cliente B2B asociado a esta franquicia';
    }

    // Validar campos de pago cuando hay pago
    if (order.paymentStatus === 'PARTIAL') {
      if (!order.paymentAmount || parseFloat(order.paymentAmount) <= 0) {
        newErrors.paymentAmount = t('payment_amount_required');
      }
      if (!order.paymentMethod) {
        newErrors.paymentMethod = t('payment_method_required');
      }
    }
    
    if (order.paymentStatus === 'PAID') {
      if (!order.paymentMethod) {
        newErrors.paymentMethod = t('payment_method_required');
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);
      
      // Preparar items según estructura del backend (igual que proformas)
      const itemsToSave = order.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountType: item.discountType || DISCOUNT_TYPE.PERCENTAGE, // Default PERCENTAGE
        discountValue: item.discountValue || 0.0
      }));

      // Usar la estructura correcta para órdenes (manteniendo consistencia en items pero con status correcto)
      const orderToSave = {
        clientId: parseInt(order.clientId),
        branchId: parseInt(order.branchId),
        destinationLocationId:
          isDestinationScoped
            ? Number(fixedDestinationLocationId)
            : selectedClient?.clientType === 'FRANCHISE' && selectedClient?.defaultDestinationLocationId
              ? Number(selectedClient.defaultDestinationLocationId)
              : null,
        status: isDestinationScoped ? 'PENDING' : order.status, // Status de orden: PENDING, FINALIZED, CANCELLED
        notes: order.notes || null,
        items: itemsToSave,
        // Campos específicos de órdenes
        orderDate: order.orderDate?.toISOString(),
        deliveryDate: order.deliveryDate?.toISOString() || null,
        paymentStatus: isDestinationScoped ? 'UNPAID' : order.paymentStatus, // Status de pago: UNPAID, PARTIAL, PAID
        creditDays: parseInt(order.creditDays) || 30, // Asegurar que se envíe creditDays
        // Solo incluir campos de pago si hay pago
        ...(order.paymentStatus === 'PARTIAL' || order.paymentStatus === 'PAID' ? {
          paymentAmount: order.paymentStatus === 'PARTIAL' 
            ? parseFloat(order.paymentAmount) || 0 
            : calculateTotal(), // Para pago total, usar el total de la orden
          paymentMethod: order.paymentMethod,
          paymentNotes: order.paymentNotes || null
        } : {})
      };
      
      await onSave(orderToSave);
      
      // Solo cerrar y resetear después de éxito
      setOrder({
        clientId: '',
        clientName: '',
        orderDate: dayjs(),
        deliveryDate: null,
        status: 'PENDING',
        notes: '',
        items: [],
        creditDays: 30, // Valor por defecto
        paymentStatus: 'UNPAID',
        paymentAmount: '',
        paymentMethod: 'TRANSFER',
        paymentNotes: '',
        branchId: '',
        branchName: '',
        destinationLocationId: ''
      });
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      SnackbarUtils.error(t('error_saving_order'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setOrder({
      clientId: '',
      clientName: '',
      orderDate: dayjs(),
      deliveryDate: null,
      status: 'PENDING',
      notes: '',
      items: [],
      creditDays: 30, // Valor por defecto
      paymentStatus: 'UNPAID',
      paymentAmount: '',
      paymentMethod: 'TRANSFER',
      paymentNotes: '',
      branchId: '',
      branchName: '',
      destinationLocationId: ''
    });
    setErrors({});
    onClose();
  };

  const selectedClient = clients.find(c => c.id === order.clientId);
  const visibleClients = isDestinationScoped
    ? clients.filter(client =>
        client.clientType === 'FRANCHISE' &&
        Number(client.defaultDestinationLocationId) === Number(fixedDestinationLocationId)
      )
    : clients;
  const assignedFranchiseLocation = destinationLocations.find(
    location => Number(location.id) === Number(selectedClient?.defaultDestinationLocationId)
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('add_b2b_order')}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                {t('order_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.clientId}>
                <InputLabel>{t('client')} *</InputLabel>
                <Select
                  value={order.clientId}
                  label={`${t('client')} *`}
                  onChange={e => {
                    const client = clients.find(c => c.id === e.target.value);
                    handleFieldChange('clientId', e.target.value);
                    handleFieldChange('clientName', client?.nombreFantasia || client?.razonSocial || client?.businessName || '');
                    
                    // Precargar días de crédito del cliente
                    if (client?.creditDays) {
                      handleFieldChange('creditDays', client.creditDays);
                    }
                    handleFieldChange(
                      'destinationLocationId',
                      client?.clientType === 'FRANCHISE' ? (client.defaultDestinationLocationId || '') : ''
                    );
                    
                    // Limpiar productos cuando cambia el cliente
                    setProducts([]);
                  }}
                  disabled={loadingClients}
                >
                  {loadingClients ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      {t('loading')}...
                    </MenuItem>
                  ) : (
                    visibleClients.map(client => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.nombreFantasia || client.razonSocial || client.businessName || `Cliente ${client.id}`}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.clientId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.clientId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.branchId}>
                <InputLabel>Sucursal origen *</InputLabel>
                <Select
                  value={order.branchId}
                  label="Sucursal origen *"
                  onChange={e => {
                    const branch = locations.find(b => b.id === e.target.value);
                    handleFieldChange('branchId', e.target.value);
                    handleFieldChange('branchName', branch?.name || '');
                    
                    // Limpiar productos cuando cambia la sucursal
                    setProducts([]);
                  }}
                  disabled={loadingLocations}
                >
                  {loadingLocations ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      {t('loading')}...
                    </MenuItem>
                  ) : (
                    locations.map(branch => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.branchId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.branchId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {selectedClient?.clientType === 'FRANCHISE' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  disabled
                  label="Franquicia asignada"
                  value={fixedDestinationLocationName || assignedFranchiseLocation?.name || ''}
                  error={!!errors.destinationLocationId}
                  helperText={errors.destinationLocationId || 'La reposición se registrará automáticamente en la franquicia asociada al cliente.'}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <DatePicker
                label={t('order_date')}
                value={order.orderDate}
                onChange={(newValue) => handleFieldChange('orderDate', newValue)}
                slotProps={{ textField: { fullWidth: true, error: !!errors.orderDate } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label={t('delivery_date')}
                value={order.deliveryDate}
                onChange={(newValue) => handleFieldChange('deliveryDate', newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {!isDestinationScoped && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('status')}</InputLabel>
                  <Select
                    value={order.status}
                    label={t('status')}
                    onChange={e => handleFieldChange('status', e.target.value)}
                  >
                    <MenuItem value="PENDING">{t('pending')}</MenuItem>
                    <MenuItem value="FINALIZED">{t('completed')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label={t('notes')}
                fullWidth
                multiline
                rows={3}
                value={order.notes}
                onChange={e => handleFieldChange('notes', e.target.value)}
              />
            </Grid>

            {/* Descuento global del cliente */}
            {selectedClient && selectedClient.globalDiscount > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    {t('client_global_discount')}: {selectedClient.globalDiscount}%
                  </Typography>
                </Box>
              </Grid>
            )}

            {!isDestinationScoped && (
              <>
                {/* Información de pago */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                    {t('payment_configuration')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('payment_status')}</InputLabel>
                    <Select
                      value={order.paymentStatus}
                      label={t('payment_status')}
                      onChange={e => {
                        handleFieldChange('paymentStatus', e.target.value);
                        // Limpiar campos de pago cuando no hay pago
                        if (e.target.value === 'UNPAID') {
                          handleFieldChange('paymentAmount', '');
                          handleFieldChange('paymentMethod', 'TRANSFER');
                          handleFieldChange('paymentNotes', '');
                        }
                        // Limpiar monto cuando cambia a pago total (el monto será el total)
                        if (e.target.value === 'PAID') {
                          handleFieldChange('paymentAmount', '');
                        }
                      }}
                    >
                      <MenuItem value="UNPAID">{t('unpaid')}</MenuItem>
                      <MenuItem value="PARTIAL">{t('partial_payment')}</MenuItem>
                      <MenuItem value="PAID">{t('paid')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12} md={isDestinationScoped ? 4 : 6}>
              <TextField
                label={t('credit_days')}
                fullWidth
                type="number"
                value={order.creditDays}
                onChange={e => handleFieldChange('creditDays', e.target.value)}
                placeholder="0"
                InputProps={{
                  endAdornment: t('days')
                }}
              />
            </Grid>

            {!isDestinationScoped && (order.paymentStatus === 'PARTIAL' || order.paymentStatus === 'PAID') && (
              <>
                {order.paymentStatus === 'PARTIAL' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('payment_amount')}
                      fullWidth
                      type="number"
                      value={order.paymentAmount}
                      onChange={e => {
                        const value = parseFloat(e.target.value) || 0;
                        const total = calculateTotal();
                        if (value > total) {
                          SnackbarUtils.error(t('payment_amount_exceeds_total'));
                          handleFieldChange('paymentAmount', total);
                        } else {
                          handleFieldChange('paymentAmount', e.target.value);
                        }
                      }}
                      placeholder="0.00"
                      error={!!errors.paymentAmount}
                      helperText={errors.paymentAmount || `${t('max_amount')}: ${formatMoney(calculateTotal())}`}
                      InputProps={{
                        startAdornment: '$'
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.paymentMethod}>
                    <InputLabel>{t('payment_method')}</InputLabel>
                    <Select
                      value={order.paymentMethod}
                      label={t('payment_method')}
                      onChange={e => handleFieldChange('paymentMethod', e.target.value)}
                    >
                      <MenuItem value="CASH">{t('cash')}</MenuItem>
                      <MenuItem value="TRANSFER">{t('transfer')}</MenuItem>
                      <MenuItem value="CHECK">{t('check')}</MenuItem>
                      <MenuItem value="CARD">{t('credit_card')}</MenuItem>
                    </Select>
                    {errors.paymentMethod && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.paymentMethod}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('payment_notes_placeholder')}
                    fullWidth
                    multiline
                    rows={2}
                    value={order.paymentNotes}
                    onChange={e => handleFieldChange('paymentNotes', e.target.value)}
                    placeholder={t('payment_notes_placeholder')}
                  />
                </Grid>
              </>
            )}

            {/* Items del pedido */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                {t('order_items')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <Autocomplete
                fullWidth
                options={products}
                getOptionLabel={(option) => option.name || ''}
                value={products.find(p => p.id === newItem.productId) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const correctPrice = getCorrectPrice(newValue.id, newValue.price || 0);
                    const discountInfo = getCorrectDiscount(newValue.id, order.clientId, newValue.price || 0);
                    
                    console.log(`Producto seleccionado: ${newValue.name}`);
                    console.log(`Precio de sucursal: ${formatMoney(correctPrice)}`);
                    console.log(`Descuento aplicado: ${discountInfo.value} (${discountInfo.type})`);
                    
                    handleItemFieldChange('productId', newValue.id);
                    handleItemFieldChange('productName', newValue.name);
                    handleItemFieldChange('unitPrice', correctPrice);
                    
                    // Solo aplicar descuento automático si el usuario no lo ha modificado manualmente
                    if (!newItem.isDiscountManuallyModified) {
                      handleItemFieldChange('discountValue', discountInfo.value);
                      handleItemFieldChange('discountType', discountInfo.type);
                    }
                  } else {
                    handleItemFieldChange('productId', '');
                    handleItemFieldChange('productName', '');
                    handleItemFieldChange('unitPrice', '');
                    // Solo resetear descuento si no fue modificado manualmente
                    if (!newItem.isDiscountManuallyModified) {
                      handleItemFieldChange('discountValue', '');
                      handleItemFieldChange('discountType', DISCOUNT_TYPE.PERCENTAGE);
                    }
                  }
                }}
                disabled={loadingProducts || !order.clientId || !order.branchId}
                loading={loadingProducts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('product')}
                    placeholder={!order.clientId ? t('select_client_first') : !order.branchId ? t('select_branch_first') : t('search_product')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.name}
                      </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatMoney(option.price)}{option.category?.name ? ` - ${option.category.name}` : ''}
                    </Typography>
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  const filtered = options.filter(option =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.category?.name && option.category.name.toLowerCase().includes(inputValue.toLowerCase()))
                  );
                  return filtered;
                }}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <TextField
                label={t('quantity')}
                fullWidth
                type="number"
                value={newItem.quantity || ''}
                onChange={e => handleItemFieldChange('quantity', parseInt(e.target.value) || 0)}
                placeholder="0"
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                label={t('unit_price')}
                fullWidth
                type="number"
                value={newItem.unitPrice || ''}
                onChange={e => handleItemFieldChange('unitPrice', parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={true}
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                label={t('total')}
                fullWidth
                value={newItem.totalPrice}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12} md={3.5}>
              <Button
                variant="outlined"
                startIcon={getIcon('checkmarkDone')}
                onClick={handleAddItem}
                disabled={!isItemFormValid()}
                fullWidth
                sx={{ height: '56px' }}
              >
                {t('add')}
              </Button>
            </Grid>

            {/* Tabla de items */}
            {order.items.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('items_list')}
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('product')}</TableCell>
                        <TableCell align="center">{t('quantity')}</TableCell>
                        <TableCell align="center">{t('unit_price')}</TableCell>
                        <TableCell align="center">{t('subtotal')}</TableCell>
                        <TableCell align="center">{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {item.productName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {item.quantity}
                            </TableCell>
                            <TableCell align="center">
                              {formatMoney(item.unitPrice)}
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold">
                                {formatMoney(item.totalPrice)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                onClick={() => handleRemoveItem(index)}
                                color="error"
                              >
                                {getIcon('trash')}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="h6" color="primary.main">
                    {t('total')}: {formatMoney(calculateTotal())}
                  </Typography>
                </Box>
              </Grid>
            )}

            {errors.items && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">{errors.items}</Typography>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t('cancel')}</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!isFormValid() || saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {saving ? t('saving') : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddB2BOrderModal;
